import StatefulSettings, { STATE_KEY_HISTORY_PENALTIES } from "../controllers/StatefulSettings";
import {
  IPenaltyItem,
  IPenaltyTimerInfo,
  PenaltyItem,
  PenaltyBasketItem,
  AuditMessage,
  IRollPenalty,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  ItemTypes,
  TraceLevel,
  PenaltyType,
  PenaltyPointConversionType,
  CountType,
} from "../ts/enums";
import { getRandom, getRandomItemWithCache } from "../utilities/utils";
import concat from "lodash/concat";

/**
 * PenaltyManager - Handles all penalty-related operations including:
 * - Penalty completion and processing
 * - Bank charges and vote count increases
 * - Penalty point conversion and spending
 * - System-generated penalty application
 */
export class PenaltyManager {
  private settings!: StatefulSettings;

  /**
   * Initialize the PenaltyManager with required dependencies
   */
  public initialize(settings: StatefulSettings): void {
    this.settings = settings;
  }

  /**
   * Completes a penalty item and applies its effects
   */
  public completePenaltyItem(basketItem: PenaltyBasketItem): void {
    const creditInfo = this.settings.getTaskSchedule();
    const item = creditInfo[basketItem.key];
    if (!item) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Penalty,
          basketItem.name,
          `Penalty item not found in schedule: ${basketItem.key}`,
          TraceLevel.warning
        )
      );
      return;
    }

    item.isPaused = false;
    item.isComplete = true;
    item.completedTime = new Date();
    delete item.timerTicks;
    creditInfo[item.key] = item;

    // Use the stored timer info directly as the penalty definition source.
    // IPenaltyTimerInfo extends IPenaltyItem, so all penalty properties
    // (penaltyType, count, taskVotes, etc.) are already present on `item`.
    // Previously this code called getPenalty(item.key) here, which fails for
    // game-result penalties because their keys are compound (originalKey + uuid).
    // That failure caused an early return before updateTaskState was called,
    // so the isComplete flag was never persisted and the penalty reappeared on reload.
    const penaltyItem = item as IPenaltyTimerInfo;

    const bank = this.settings.getBankBalance();
    if (penaltyItem.penaltyType === PenaltyType.bankChargeAmount) {
      this.settings.decrementBank(
        penaltyItem.count as number,
        `Penalty ${penaltyItem.name}`
      );
      this.settings.payOwner(
        (penaltyItem.count as number) > bank
          ? bank
          : (penaltyItem.count as number)
      );
    } else if (penaltyItem.penaltyType === PenaltyType.bankChargePercent) {
      const amt = (penaltyItem.count || 0) * bank;
      this.settings.decrementBank(amt, `Penalty ${penaltyItem.name}`);
      this.settings.payOwner(amt);
    } else if (penaltyItem.penaltyType === PenaltyType.increaseVoteCount) {
      if (penaltyItem.taskVotes) {
        const voteTask = penaltyItem.taskVotes[0];
        if (voteTask && voteTask.taskKey) {
          this.settings.voteUpdate(voteTask);
        }
      }
    } else if (penaltyItem.penaltyType === PenaltyType.increaseTargetAmount) {
      const p = this.settings.getCheckoutPermission();
      if (p && p.requiredCredit) {
        p.requiredCredit += penaltyItem.count as number;
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.DailyChecks,
            "Penalty",
            "Checkout Target increased to new Checkout Total of " +
            p.requiredCredit,
            TraceLevel.info,
            true
          )
        );
        this.settings.permissionEditItem(p, true);
        return;
      }
    }

    const displayName = basketItem.name;
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Penalty,
        displayName,
        "Complete",
        TraceLevel.info,
        true
      )
    );
    this.settings.updateTaskState(item);
  }

  /**
   * Spends penalty points by applying penalties or converting to bank charges
   */
  public async spendPenaltyPoints(
    rollPenalties?: IRollPenalty[]
  ): Promise<void> {
    const penaltyPoints = this.settings.getControl().penaltyPoints;
    let controlTotalRemaining = penaltyPoints;
    let rollTotalRemaining = 0;
    rollPenalties
      ?.map((p) => p.points)
      .forEach((p) => (rollTotalRemaining += p));
    const rollPenaltyReasons = rollPenalties?.map((p) => p.reason);
    let totalRemaining = rollTotalRemaining + controlTotalRemaining;
    if (totalRemaining === 0) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.DailyChecks,
          "Penalty Points",
          "No penalty points to spend today",
          TraceLevel.info
        )
      );
      return; //nothing to do as no points to spend
    }
    if (totalRemaining !== 0) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.DailyChecks,
          "Penalty Points",
          `Spending ${totalRemaining} Penalty Points`,
          TraceLevel.info
        )
      );
      if (
        this.settings.getGeneralSettings().penaltyPointConversionType ===
        PenaltyPointConversionType.decreaseBankBalance
      ) {
        this.settings.decrementBank(
          totalRemaining,
          "Penalty Points converted to Bank Balance charge"
        );
        this.settings.payOwner(totalRemaining);
        totalRemaining = 0;
      } else if (
        this.settings.getGeneralSettings().penaltyPointConversionType ===
        PenaltyPointConversionType.increaseCheckoutTarget
      ) {
        const p = this.settings.getCheckoutPermission();
        if (p && p.requiredCredit) {
          p.requiredCredit += totalRemaining;
          this.settings.auditUser(
            new AuditMessage(
              AuditActionTypes.DailyChecks,
              "Penalty Points",
              "Points converted to new Checkout Total of " + p.requiredCredit,
              TraceLevel.info
            )
          );
          this.settings.permissionEditItem(p, true);
          totalRemaining = 0;
        }
      } else {
        if (totalRemaining > 0 && this.settings.canPerformSomeTasksForToday()) {
          const penalties = this.settings.getAllowedPenalties();
          if (penalties && penalties.length > 0) {
            const basket = this.settings.getBasketItems(true);
            const penaltiesApplied: string[] = concat(
              basket.pendingItems
                .filter((p) => p.itemType === ItemTypes.penalty)
                .map((p) => p.key),
              basket.penalties.map((p) => p.key)
            );

            let matching = penalties.filter(
              (p) =>
                p.points <= totalRemaining &&
                penaltiesApplied.indexOf(p.key) === -1 &&
                this.settings.canAddTask(p).isOk &&
                PenaltyItem.IsPenaltyTask(p)
            );

            while (totalRemaining > 0 && matching.length > 0) {
              const selectedPenalty = this.getMaxPenaltyToApply(matching);
              if (selectedPenalty) {
                penaltiesApplied.push(selectedPenalty.key);
                totalRemaining -= selectedPenalty.points;
                const reason = [];
                if (totalRemaining === 0) {
                  if (controlTotalRemaining > 0) {
                    reason.push("Previous Earned Penalties");
                  }
                  if (rollTotalRemaining > 0 && rollPenaltyReasons) {
                    for (let i = 0; i < selectedPenalty.points; i++)
                      reason.push(rollPenaltyReasons[i]);
                  }
                  controlTotalRemaining = 0;
                  rollTotalRemaining = 0;
                } else if (controlTotalRemaining >= selectedPenalty.points) {
                  // a penalty is applied by another is possible to find after with the remainder
                  reason.push("Previous Earned Penalties");
                  controlTotalRemaining =
                    controlTotalRemaining - selectedPenalty.points;
                } else {
                  if (controlTotalRemaining > 0) {
                    reason.push("Previous Earned Penalties");
                  }
                  // the remainder that picked up roll penalties
                  const todayPointsSpent =
                    selectedPenalty.points - controlTotalRemaining;
                  if (todayPointsSpent > 0 && rollPenaltyReasons) {
                    for (let i = 0; i < todayPointsSpent; i++)
                      reason.push(rollPenaltyReasons[i]);
                    rollPenaltyReasons.splice(0, todayPointsSpent);
                  }
                  controlTotalRemaining = 0;
                  rollTotalRemaining = rollTotalRemaining - todayPointsSpent;
                }
                const newRemaining = totalRemaining;
                this.addSystemGeneratedPenaltyItem(
                  selectedPenalty,
                  reason.filter((r) => r).join(", ")
                );
                this.settings.auditUser(
                  new AuditMessage(
                    AuditActionTypes.DailyChecks,
                    selectedPenalty.name,
                    "Penalty Added",
                    TraceLevel.info
                  )
                );
                matching = penalties.filter(
                  (p) =>
                    p.points <= newRemaining &&
                    penaltiesApplied.indexOf(p.key) === -1
                );
              } else {
                break;
              }
            }
          }
        }
      }
    }
    if (totalRemaining > 0)
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.DailyChecks,
          "Penalty Points",
          `Rolling ${totalRemaining} Penalty Points to the next day`,
          TraceLevel.warning
        )
      );
    this.settings.addPenaltyPoint("spent on penalties", totalRemaining, true);
  }

  /**
   * Gets the maximum penalty that can be applied from a list of matching penalties
   */
  private getMaxPenaltyToApply(
    matching: IPenaltyItem[]
  ): IPenaltyItem | undefined {
    const sortedList = matching.sort((a, b) => b.points - a.points);
    const highestPenaltyToApply = sortedList[0];
    const highestPoints = highestPenaltyToApply.points;
    const penaltiesWithSamePoints = sortedList.filter(
      (p) => p.points === highestPoints
    );
    const selectedPenalty = getRandomItemWithCache(
      penaltiesWithSamePoints,
      STATE_KEY_HISTORY_PENALTIES,
      (p) => p.key,
      this.settings,
      true
    );
    return selectedPenalty;
  }

  /**
   * Adds a system-generated penalty item to the schedule
   */
  public addSystemGeneratedPenaltyItem(
    penalty: IPenaltyItem,
    reason: string
  ): void {
    const penaltyClone = { ...penalty };
    if (penaltyClone.count)
      penaltyClone.count = getRandom(
        penaltyClone.count * 0.75,
        penaltyClone.count * 1.5
      );
    this.settings.addPenaltyToSchedule(
      penaltyClone,
      "This penalty was added to the session for penalty points incurred over the last day and has been selected based on the availablity of the member via their diary, the kinks and limits and the settings of the penalties. This penalty was the maximum available for the points required to be spent.",
      undefined,
      { reason, ownerAdded: false, countType: CountType.none }
    );
  }
}
